import { useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import Navbar from '../../components/feature/Navbar';
import Footer from '../../components/feature/Footer';
import { useLanguageFromPath } from '../../hooks/useLanguageFromPath';
import { lp, getLangFromPath } from '../../utils/routes';
import { useLocation } from 'react-router-dom';
import { SITE_ORIGIN } from '@/config/siteUrl';

export default function PlansPage() {
  useLanguageFromPath();
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const lang = getLangFromPath(location.pathname);
  const [openFaq, setOpenFaq] = useState<number | null>(null);
  const [selected, setSelected] = useState<'plan1' | 'plan2' | null>(null);

  useEffect(() => {
    const isEs = i18n.language === 'es';
    document.title = isEs
      ? 'Planes y Precios | Dos Opciones Transparentes | Kailac'
      : 'Plans & Pricing | Two Clear Advisory Plans | Kailac';
    const meta = document.querySelector('meta[name="description"]');
    if (meta) meta.setAttribute('content', isEs
      ? 'Dos planes de asesoría transparentes para dueños de negocios calificados. Plan Inicial: $500 + 4.3% después del financiamiento. Servicio Completo: 3% único. Sin costos ocultos.'
      : 'Two transparent advisory plans for qualified business owners. Plan 1: $500 + 4.3% success fee after funding. Plan 2: 3% upfront full-service concierge. No hidden fees. Subject to eligibility.');
    const canonical = document.querySelector('link[rel="canonical"]');
    if (canonical) canonical.setAttribute('href', isEs ? `${SITE_ORIGIN}/es/planes` : `${SITE_ORIGIN}/plans`);
    let hreflangEn = document.querySelector('link[data-page="plans"][hreflang="en"]') as HTMLLinkElement | null;
    let hreflangEs = document.querySelector('link[data-page="plans"][hreflang="es"]') as HTMLLinkElement | null;
    if (!hreflangEn) { hreflangEn = document.createElement('link'); hreflangEn.setAttribute('rel', 'alternate'); hreflangEn.setAttribute('hreflang', 'en'); hreflangEn.setAttribute('data-page', 'plans'); document.head.appendChild(hreflangEn); }
    if (!hreflangEs) { hreflangEs = document.createElement('link'); hreflangEs.setAttribute('rel', 'alternate'); hreflangEs.setAttribute('hreflang', 'es'); hreflangEs.setAttribute('data-page', 'plans'); document.head.appendChild(hreflangEs); }
    hreflangEn.setAttribute('href', `${SITE_ORIGIN}/plans`);
    hreflangEs.setAttribute('href', `${SITE_ORIGIN}/es/planes`);
  }, [i18n.language]);

  const plan1Features = [
    { key: 'plans_feature_1', highlight: false },
    { key: 'plans_feature_2', highlight: true },
    { key: 'plans_feature_3', highlight: true },
    { key: 'plans_feature_4', highlight: false },
    { key: 'plans_feature_5', highlight: false },
    { key: 'plans_feature_6', highlight: false },
    { key: 'plans_feature_7', highlight: false },
    { key: 'plans_feature_8', highlight: false },
  ];

  const plan2Features = [
    { key: 'plans_feature_f1', highlight: true },
    { key: 'plans_feature_f2', highlight: true },
    { key: 'plans_feature_f3', highlight: false },
    { key: 'plans_feature_f4', highlight: true },
    { key: 'plans_feature_f5', highlight: false },
    { key: 'plans_feature_f6', highlight: false },
    { key: 'plans_feature_f7', highlight: false },
    { key: 'plans_feature_f8', highlight: false },
    { key: 'plans_feature_f9', highlight: false },
    { key: 'plans_feature_f10', highlight: false },
  ];

  const faqs = [
    { qKey: 'plans_faq_1_q', aKey: 'plans_faq_1_a' },
    { qKey: 'plans_faq_2_q', aKey: 'plans_faq_2_a' },
    { qKey: 'plans_faq_3_q', aKey: 'plans_faq_3_a' },
    { qKey: 'plans_faq_4_q', aKey: 'plans_faq_4_a' },
    { qKey: 'plans_faq_5_q', aKey: 'plans_faq_5_a' },
  ];

  const starterIfItems = [
    'plans_starter_if_1',
    'plans_starter_if_2',
    'plans_starter_if_3',
    'plans_starter_if_4',
    'plans_starter_if_5',
  ];

  const fullIfItems = [
    'plans_full_if_1',
    'plans_full_if_2',
    'plans_full_if_3',
    'plans_full_if_4',
    'plans_full_if_5',
  ];

  const tableRows = [
    { labelKey: 'plans_row_upfront', p1: '$500 flat', p2: '3% of amount' },
    { labelKey: 'plans_row_fee', p1: '4.3% (after funding)', p2: 'None — in 3%' },
    { labelKey: 'plans_row_cost25', p1: '~$1,575', p2: '~$750' },
    { labelKey: 'plans_row_cost50', p1: '~$2,650', p2: '~$1,500' },
    { labelKey: 'plans_row_cost100', p1: '~$4,800', p2: '~$3,000' },
    { labelKey: 'plans_row_support', p1Key: 'plans_row_support_p1', p2Key: 'plans_row_support_p2' },
    { labelKey: 'plans_row_priority', p1Key: 'plans_row_priority_p1', p2Key: 'plans_row_priority_p2' },
    { labelKey: 'plans_row_docs', p1Key: 'plans_row_docs_p1', p2Key: 'plans_row_docs_p2' },
    { labelKey: 'plans_row_access', p1Key: 'plans_row_access_p1', p2Key: 'plans_row_access_p2' },
    { labelKey: 'plans_row_nofund', nofundMarketing: true },
    { labelKey: 'plans_row_bilingual', p1Key: 'plans_row_bilingual_p1', p2Key: 'plans_row_bilingual_p2' },
  ];

  return (
    <div className="min-h-screen bg-[#FCFAFD] pb-16 lg:pb-0">
      <Navbar />

      {/* Hero */}
      <section className="bg-[#F8F3FB] pt-32 pb-20 px-6 lg:px-8">
        <div className="max-w-4xl mx-auto text-center">
          <p className="text-xs font-semibold text-[#4F2D63] uppercase tracking-widest mb-4">{t('plans_eyebrow')}</p>
          <h1 className="text-5xl lg:text-6xl font-bold text-gray-900 mb-5 leading-tight">
            {t('plans_hero_title_1')}<br />
            <span className="text-[#4F2D63]">{t('plans_hero_title_2')}</span>
          </h1>
          <p className="text-base text-gray-500 max-w-2xl mx-auto mb-4">
            {t('plans_hero_subtitle')}
          </p>
          <p className="text-xs text-gray-400 italic">{t('plans_hero_disclaimer')}</p>
        </div>
      </section>

      {/* Plan Cards */}
      <section className="py-20 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-8">
            {/* Plan 1 */}
            <div
              className={`rounded-2xl border-2 p-8 flex flex-col transition-all cursor-pointer ${selected === 'plan1' ? 'border-[#4F2D63] bg-[#F8F3FB]' : 'border-[#E9D9F2] bg-white hover:border-[#DBB6EE]'}`}
              onClick={() => setSelected('plan1')}
            >
              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className="text-xs font-bold text-[#4F2D63] uppercase tracking-widest mb-2">{t('plans_starter_label')}</p>
                  <p className="text-sm text-gray-500 mb-3">{t('plans_starter_best_for')}</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-gray-900">$500</span>
                  </div>
                  <p className="text-base font-semibold text-[#4F2D63] mt-1">{t('plans_starter_after')}</p>
                  <p className="text-sm text-gray-500 italic mt-1">{t('plans_starter_note')}</p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${selected === 'plan1' ? 'border-[#4F2D63] bg-[#4F2D63]' : 'border-gray-300'}`}>
                  {selected === 'plan1' && <i className="ri-check-line text-white text-xs"></i>}
                </div>
              </div>

              <div className="bg-[#F8F3FB] rounded-xl px-4 py-3 mb-6">
                <p className="text-xs font-semibold text-[#4F2D63] mb-0.5">{t('plans_starter_example_label')}</p>
                <p className="text-sm font-bold text-[#3E234F]">{t('plans_starter_example')}</p>
              </div>

              <div className="h-px bg-gray-100 mb-6"></div>

              <ul className="space-y-3 flex-1 mb-8">
                {plan1Features.map(({ key, highlight }) => (
                  <li key={key} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#0F766E] text-sm font-bold"></i>
                    </div>
                    <span className={`text-sm leading-relaxed ${highlight ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>{t(key)}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={lp('/contact', lang)}
                className="w-full py-4 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm text-center transition-colors cursor-pointer whitespace-nowrap block"
              >
                {t('plans_starter_cta')}
              </Link>
            </div>

            {/* Plan 2 */}
            <div
              className={`rounded-2xl border-2 p-8 flex flex-col transition-all cursor-pointer relative bg-[#F8F3FB] ${selected === 'plan2' ? 'border-[#4F2D63]' : 'border-[#E9D9F2] hover:border-[#DBB6EE]'}`}
              onClick={() => setSelected('plan2')}
            >
              <div className="absolute -top-3.5 left-1/2 -translate-x-1/2">
                <span className="px-4 py-1.5 rounded-full bg-amber-500 text-white text-xs font-bold whitespace-nowrap">
                  {t('plans_best_value')}
                </span>
              </div>

              <div className="flex items-start justify-between mb-5">
                <div>
                  <p className="text-xs font-bold text-[#4F2D63] uppercase tracking-widest mb-2">{t('plans_full_label')}</p>
                  <p className="text-sm text-gray-500 mb-3">{t('plans_full_best_for')}</p>
                  <div className="flex items-baseline gap-2">
                    <span className="text-5xl font-bold text-gray-900">3%</span>
                  </div>
                  <p className="text-base font-semibold text-[#4F2D63] mt-1">{t('plans_full_after')}</p>
                  <p className="text-sm text-gray-500 italic mt-1">{t('plans_full_note')}</p>
                </div>
                <div className={`w-6 h-6 rounded-full border-2 flex items-center justify-center flex-shrink-0 mt-1 ${selected === 'plan2' ? 'border-[#4F2D63] bg-[#4F2D63]' : 'border-gray-300'}`}>
                  {selected === 'plan2' && <i className="ri-check-line text-white text-xs"></i>}
                </div>
              </div>

              <div className="bg-white rounded-xl px-4 py-3 mb-6 border border-[#E9D9F2]">
                <p className="text-xs font-semibold text-[#4F2D63] mb-0.5">{t('plans_full_example_label')}</p>
                <p className="text-sm font-bold text-[#3E234F]">{t('plans_full_example')}</p>
              </div>

              <div className="h-px bg-[#E9D9F2] mb-6"></div>

              <ul className="space-y-3 flex-1 mb-8">
                {plan2Features.map(({ key, highlight }) => (
                  <li key={key} className="flex items-start gap-3">
                    <div className="w-5 h-5 flex items-center justify-center flex-shrink-0 mt-0.5">
                      <i className="ri-check-line text-[#0F766E] text-sm font-bold"></i>
                    </div>
                    <span className={`text-sm leading-relaxed ${highlight ? 'text-gray-900 font-medium' : 'text-gray-600'}`}>{t(key)}</span>
                  </li>
                ))}
              </ul>

              <Link
                to={lp('/contact', lang)}
                className="w-full py-4 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold text-sm text-center transition-colors cursor-pointer whitespace-nowrap block"
              >
                {t('plans_full_cta')}
              </Link>
            </div>
          </div>

          <div className="text-center mt-8">
            <p className="text-sm text-gray-500">
              {t('plans_not_sure')}{' '}
              <Link to={lp('/contact', lang)} className="text-[#4F2D63] font-semibold hover:text-[#3E234F] cursor-pointer">
                {t('plans_advisor_link')}
              </Link>
            </p>
            <p className="text-xs text-gray-400 mt-3 italic max-w-2xl mx-auto">
              {t('plans_disclaimer_cards')}
            </p>
          </div>
        </div>
      </section>

      {/* Comparison Table */}
      <section className="py-16 bg-[#F8F3FB] px-6 lg:px-8">
        <div className="max-w-4xl mx-auto">
          <div className="text-center mb-10">
            <h2 className="text-3xl font-bold text-gray-900 mb-2">{t('plans_compare_title')}</h2>
            <p className="text-sm text-gray-500">{t('plans_compare_subtitle')}</p>
          </div>
          <div className="bg-white rounded-2xl border border-gray-100 overflow-hidden">
            <table className="w-full">
              <thead>
                <tr className="border-b border-[#E9D9F2]">
                  <th className="py-4 px-6 text-left text-sm font-semibold text-gray-600 w-1/2">{t('plans_compare_feature')}</th>
                  <th className="py-4 px-6 text-center text-sm font-semibold text-[#4F2D63]">{t('plans_starter_label')}</th>
                  <th className="py-4 px-6 text-center text-sm font-semibold text-[#1F0D35] bg-[#F8F3FB]">{t('plans_full_label')}</th>
                </tr>
              </thead>
              <tbody>
                {tableRows.map((row, i) => {
                  const { labelKey, p1, p2, p1Key, p2Key, nofundMarketing } = row;
                  return (
                  <tr key={labelKey} className={`border-b border-[#E9D9F2] ${i % 2 === 0 ? '' : 'bg-[#F8F3FB]/50'}`}>
                    <td className="py-3.5 px-6 text-sm text-gray-700 font-medium">{t(labelKey)}</td>
                    <td className="py-3.5 px-6 text-center text-sm text-[#4F2D63]">
                      {nofundMarketing ? (
                        <span className="text-sm font-medium text-[#4F2D63]">{t('plans_row_nofund_marketing')}</span>
                      ) : p1Key ? (
                        <span
                          className="inline-flex justify-center mx-auto"
                          role="img"
                          aria-label={t(p1Key)}
                        >
                          <i className="ri-check-line text-[#0F766E] text-xl" aria-hidden />
                        </span>
                      ) : (
                        p1
                      )}
                    </td>
                    <td className="py-3.5 px-6 text-center text-sm text-[#1F0D35] font-medium bg-[#F8F3FB]/30">
                      {nofundMarketing ? (
                        <span className="text-sm font-medium text-[#1F0D35]">{t('plans_row_nofund_marketing')}</span>
                      ) : p2Key ? (
                        <span
                          className="inline-flex justify-center mx-auto"
                          role="img"
                          aria-label={t(p2Key)}
                        >
                          <i className="ri-check-line text-[#0F766E] text-xl" aria-hidden />
                        </span>
                      ) : (
                        p2
                      )}
                    </td>
                  </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
          <p className="text-xs text-gray-400 italic text-center mt-4">
            {t('plans_compare_disclaimer')}
          </p>
        </div>
      </section>

      {/* Who Each Plan Is For */}
      <section className="py-16 px-6 lg:px-8">
        <div className="max-w-5xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">{t('plans_right_title')}</h2>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="bg-[#F8F3FB] rounded-2xl p-7 border border-[#E9D9F2]">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#E9D9F2]">
                  <i className="ri-user-3-line text-[#4F2D63] text-lg"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{t('plans_starter_if_title')}</h3>
              </div>
              <ul className="space-y-3">
                {starterIfItems.map((key) => (
                  <li key={key} className="flex items-start gap-2 text-sm text-gray-700">
                    <i className="ri-check-circle-fill text-[#0F766E] flex-shrink-0 mt-0.5"></i>
                    {t(key)}
                  </li>
                ))}
              </ul>
            </div>
            <div className="bg-[#F8F3FB] rounded-2xl p-7 border border-[#E9D9F2]">
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 flex items-center justify-center rounded-xl bg-[#E9D9F2]">
                  <i className="ri-vip-crown-line text-[#4F2D63] text-lg"></i>
                </div>
                <h3 className="text-lg font-bold text-gray-900">{t('plans_full_if_title')}</h3>
              </div>
              <ul className="space-y-3">
                {fullIfItems.map((key) => (
                  <li key={key} className="flex items-start gap-2 text-sm text-gray-700">
                    <i className="ri-check-circle-fill text-[#0F766E] flex-shrink-0 mt-0.5"></i>
                    {t(key)}
                  </li>
                ))}
              </ul>
            </div>
          </div>
        </div>
      </section>

      {/* FAQ */}
      <section className="py-16 bg-[#F8F3FB] px-6 lg:px-8">
        <div className="max-w-3xl mx-auto">
          <h2 className="text-3xl font-bold text-gray-900 text-center mb-10">{t('plans_faq_section_title')}</h2>
          <div className="space-y-3">
            {faqs.map(({ qKey, aKey }, i) => (
              <div key={qKey} className="bg-white rounded-xl border border-[#E9D9F2] overflow-hidden transition-all">
                <button onClick={() => setOpenFaq(openFaq === i ? null : i)} className="w-full flex items-center justify-between gap-4 px-6 py-5 text-left cursor-pointer">
                  <span className="text-sm font-semibold text-gray-900 leading-snug">{t(qKey)}</span>
                  <div className={`w-7 h-7 flex items-center justify-center rounded-full flex-shrink-0 transition-all ${openFaq === i ? 'bg-[#E9D9F2] rotate-45' : 'bg-gray-100'}`}>
                    <i className="ri-add-line text-gray-600 text-sm"></i>
                  </div>
                </button>
                <div className={`transition-all duration-300 ${openFaq === i ? 'max-h-64 opacity-100' : 'max-h-0 opacity-0'} overflow-hidden`}>
                  <p className="px-6 pb-5 text-sm text-gray-600 leading-relaxed">{t(aKey)}</p>
                </div>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-[#F8F3FB] px-6 lg:px-8">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-bold text-gray-900 mb-3">{t('plans_cta_title')}</h2>
          <p className="text-gray-500 mb-8 text-base">{t('plans_cta_subtitle')}</p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to={lp('/contact', lang)} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl bg-[#4F2D63] hover:bg-[#3E234F] text-white font-semibold transition-colors cursor-pointer whitespace-nowrap">
              {t('plans_cta_primary')} <i className="ri-arrow-right-line"></i>
            </Link>
            <Link to={lp('/how-it-works', lang)} className="inline-flex items-center gap-2 px-8 py-4 rounded-xl border border-[#E9D9F2] text-gray-700 hover:border-[#4F2D63] font-semibold transition-colors cursor-pointer whitespace-nowrap">
              {t('plans_cta_secondary')}
            </Link>
          </div>
          <p className="text-xs text-gray-400 mt-6 italic">{t('plans_cta_disclaimer')}</p>
        </div>
      </section>

      <Footer />
    </div>
  );
}
